import React, { useState, useEffect, useRef } from 'react';
import { ClockCircleOutlined } from '@ant-design/icons';

/**
 * Pastilla flotante con la cuenta regresiva de la ventana de pre-ofertas.
 * Cuelga del header, arriba a la derecha, y solo aparece con las ofertas
 * ABIERTAS: es angosta y no reserva espacio en el layout.
 *
 * POR QUE DESCUENTA CONTRA LA HORA DEL SERVIDOR Y NO CONTRA `Date.now()`:
 * el reloj de la computadora del visitante puede estar corrido. Ya paso en
 * versiones anteriores de la plataforma —hasta 10 segundos— y el efecto era el
 * peor posible: a alguien que esperaba para ofertar sobre la hora se le cerraba
 * la pre-oferta antes de tiempo. `estado_ofertas.ahora` trae el instante del
 * servidor, se mide el desfasaje UNA vez al montar y todo el descuento va
 * contra esa referencia.
 *
 * Y el reloj sincronizado es solo para MOSTRAR: cuando llega a cero no habilita
 * ni deshabilita nada por su cuenta, recarga la pagina para que el backend
 * vuelva a decidir. La decision de si se puede ofertar es del servidor, siempre.
 */

const UN_SEGUNDO = 1000;

// Fallback de separacion derecha, solo hasta poder medir el contenido.
const MARGEN_DERECHO = 24;

// El header al que se cuelga la pastilla. Se MIDE del DOM en vez de calcular su
// alto con constantes: el alto depende del tipo de cabecera, de si el logo es
// alto, de la franja que lleva debajo y de si la pantalla es chica —en mobile
// colapsa—. Cualquier numero fijo queda desfasado o tapado en cuanto algo de
// eso cambia, que fue exactamente lo que paso.
const SELECTOR_HEADER = '.site-header';


/**
 * Momento en que el estado de la ventana CAMBIA: la apertura si todavia no
 * abrio, el cierre si esta abierta. Es lo que hay que vigilar para recargar.
 */
const momentoDeCambio = (estadoOfertas) => {
  const e = estadoOfertas?.estado;
  return e === 'programadas' ? estadoOfertas?.desde
       : e === 'abiertas'    ? estadoOfertas?.hasta
       : null;
};

/**
 * Momento que el reloj MUESTRA: solo el cierre, y solo con las ofertas
 * ABIERTAS. Pedido del usuario — fuera de la ventana la ficha ya avisa con su
 * propio cartel y un reloj al lado repetia el dato.
 *
 * OJO: esto es DISPLAY, no es lo mismo que `momentoDeCambio`. Cuando las dos
 * cosas eran una sola funcion, restringir el reloj a "abiertas" se llevo puesta
 * la recarga de la APERTURA: quien tenia la ficha abierta a las 07:55 seguia
 * viendo "las ofertas abren a las 08:00" pasadas las 08:00, mientras el que
 * entraba de nuevo veia el formulario. AGENTS.md es explicito en que la fecha
 * abre y cierra sola.
 */
const objetivoDe = (estadoOfertas) => (
  estadoOfertas?.estado === 'abiertas' ? estadoOfertas?.hasta : null
);

/** Instante del servidor al cargar la pagina, en ms. null si no vino. */
const ahoraServidorMs = (estadoOfertas) => {
  if (!estadoOfertas?.ahora) return null;
  const t = new Date(estadoOfertas.ahora).getTime();
  return isNaN(t) ? null : t;
};


/** ms → { d, h, m, s } ya en dos digitos. */
const partes = (ms) => {
  const total = Math.max(0, Math.floor(ms / UN_SEGUNDO));
  const dd = Math.floor(total / 86400);
  const hh = Math.floor((total % 86400) / 3600);
  const mm = Math.floor((total % 3600) / 60);
  const ss = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return { d: pad(dd), h: pad(hh), m: pad(mm), s: pad(ss), dias: dd };
};

const CountdownPreOfertas = ({ estadoOfertas, top = 76, colorPrimary = '#1a5f2a',
                               bloquearRecarga = false, antesDeRecargar }) => {
  // DOS momentos distintos, y confundirlos ya costo un bug:
  //   `cambio`   -> cuando el estado da vuelta. Se vigila SIEMPRE, se muestre o
  //                 no el reloj, porque la fecha tiene que abrir y cerrar sola.
  //   `objetivo` -> lo que el reloj PINTA. Solo el cierre, solo con las ofertas
  //                 abiertas.
  const cambio   = momentoDeCambio(estadoOfertas);
  const objetivo = objetivoDe(estadoOfertas);

  // Desfasaje servidor − navegador, medido una sola vez. Si el backend no manda
  // `ahora` (respuesta vieja cacheada), queda en 0 y el countdown se comporta
  // como antes en vez de romperse.
  const offsetRef = useRef(null);
  if (offsetRef.current === null) {
    const servidor = ahoraServidorMs(estadoOfertas);
    offsetRef.current = servidor !== null ? servidor - Date.now() : 0;
  }

  // `bloquearRecarga` espejado en un ref porque el intervalo se arma UNA vez
  // (dep `objetivo`, un string) y su closure congela los props de ese render.
  // Leyendo el prop directo, el tick de las 00:00:00 seguia viendo el `false`
  // del montaje y recargaba igual encima de una oferta en vuelo: la promesa
  // central de este componente era falsa en runtime. El ref se actualiza en
  // cada render, asi que el intervalo siempre lee el valor vivo.
  const bloquearRef = useRef(bloquearRecarga);
  bloquearRef.current = bloquearRecarga;

  // Borde inferior REAL del header, medido del DOM. `ResizeObserver` porque el
  // header cambia de alto solo: colapsa en mobile, y el tipo de cabecera o un
  // logo mas grande lo mueven sin que este componente se entere. Con un `top`
  // calculado a mano quedaba tapado o despegado. El prop `top` queda de
  // fallback para cuando el header todavia no monto o no existe.
  const [topHeader, setTopHeader] = useState(null);
  const [rightHeader, setRightHeader] = useState(null);
  useEffect(() => {
    const header = document.querySelector(SELECTOR_HEADER);
    if (!header) return undefined;
    const medir = () => {
      setTopHeader(header.getBoundingClientRect().bottom);
      // Alineada con el borde derecho del CONTENIDO, no del viewport: el
      // contenedor tiene maxWidth y padding, asi que en pantallas anchas el
      // contenido termina bastante antes que la pantalla. Asi queda a plomo con
      // el boton MI PANEL y con el menu hamburguesa.
      //
      // Se busca el contenedor VISIBLE, no el primero que aparezca: el header
      // trae los dos armados —`.site-header-inner` adentro de la barra de
      // escritorio y `.site-header-mobile`— y el CSS apaga uno con
      // `display: none`. Un elemento oculto devuelve un rect en CEROS, asi que
      // medir el de escritorio en un telefono daba `right: 0` y la cuenta tiraba
      // la pastilla fuera de la pantalla: por eso desaparecia en mobile.
      const candidatos = header.querySelectorAll('.site-header-inner, .site-header-mobile');
      const visible = Array.from(candidatos)
        .find((el) => el.getBoundingClientRect().width > 0);
      if (visible) {
        const r = visible.getBoundingClientRect();
        // El padding real del contenedor, no un numero fijo: la barra de
        // escritorio usa 24px y la mobile 16px.
        const padRight = parseFloat(getComputedStyle(visible).paddingRight) || 0;
        // `clientWidth` y no `innerWidth`: innerWidth incluye el ancho del
        // scrollbar (~15px) y el rect no, asi que la pastilla quedaba corrida
        // esos pixeles en escritorio.
        const anchoUtil = document.documentElement.clientWidth;
        setRightHeader(Math.max(0, anchoUtil - r.right + padRight));
      }
    };
    medir();
    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(medir)
      : null;
    observer?.observe(header);
    // `resize` ademas del observer: al rotar el telefono cambia el viewport y
    // el header puede pasar de la barra mobile a la de escritorio.
    window.addEventListener('resize', medir);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', medir);
    };
  }, []);

  /** ms que faltan hasta `momento`, contra la hora del servidor. */
  const faltanHasta = (momento) => {
    if (!momento) return null;
    const fin = new Date(momento).getTime();
    if (isNaN(fin)) return null;
    return fin - (Date.now() + offsetRef.current);
  };

  const restanteReal = () => faltanHasta(objetivo);

  const [restante, setRestante] = useState(restanteReal);
  // Evita disparar dos reloads si el intervalo alcanza el cero mas de una vez
  // antes de que el navegador llegue a recargar.
  const recargando = useRef(false);
  // La ventana ya cerro pero habia una oferta viajando: la recarga queda
  // pendiente hasta que se libere.
  const [recargaPendiente, setRecargaPendiente] = useState(false);

  // NUNCA recarga con una oferta en vuelo. Alguien que aprieta Ofertar a las
  // 23:59:58 tiene el POST viajando cuando el contador llega a cero; recargar
  // ahi le borra la pantalla sin exito ni error, sin manera de saber si su
  // oferta entro. Y esa persona -la que oferta sobre la hora- es exactamente
  // a quien este componente existe para proteger.
  const recargarSiSePuede = () => {
    if (recargando.current) return;
    if (bloquearRef.current) { setRecargaPendiente(true); return; }
    recargando.current = true;
    // Esta recarga es AUTOMATICA, no la pidio nadie: la pagina puede tener
    // estado que asume que un full reload equivale a "el visitante volvio a
    // entrar" (el micrositio embebido resetea ahi su deep-link). Se le avisa
    // antes para que lo deje en claro.
    antesDeRecargar?.();
    // Se termino la ventana: que el backend vuelva a decidir el estado.
    window.location.reload();
  };

  // El intervalo se arma contra `cambio`, NO contra `objetivo`: con las ofertas
  // programadas no hay reloj que pintar pero igual hay que recargar cuando abre,
  // o quien ya tenia la ficha abierta se queda mirando "las ofertas abren a las
  // 08:00" pasadas las 08:00. `setRestante` sigue el objetivo visible, que con
  // las ofertas programadas es null y no dibuja nada.
  useEffect(() => {
    if (!cambio) return undefined;
    const tick = () => {
      setRestante(restanteReal());
      const ms = faltanHasta(cambio);
      if (ms !== null && ms <= 0) recargarSiSePuede();
    };
    tick();
    const id = setInterval(tick, UN_SEGUNDO);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cambio, objetivo]);

  // La oferta que estaba en vuelo termino (con exito o con error, ya se le
  // mostro el resultado): recien ahora se puede recargar.
  useEffect(() => {
    if (recargaPendiente && !bloquearRecarga) recargarSiSePuede();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recargaPendiente, bloquearRecarga]);

  if (!objetivo || restante === null || restante <= 0) return null;

  const { d, h, m, s } = partes(restante);
  const urgente = restante < 60 * 60 * UN_SEGUNDO;   // ultima hora

  // Pastilla compacta arriba a la derecha, como la muestra del usuario. La
  // franja de ancho completo tapaba media pantalla para dar un solo dato; asi
  // acompaña sin competir con el contenido de la ficha.
  return (
    <div
      role="timer"
      aria-live="off"
      className="countdown-preofertas"
      title="Tiempo restante para pre-ofertar"
      style={{
        // Pegada al borde inferior REAL del header, medido del DOM. Va inline y
        // no por CSS var justamente porque ya no hay nada que calcular: el
        // valor medido ya contempla el colapso de mobile y cualquier cambio de
        // alto. Si todavia no se midio, cae al alto declarado que pasa la
        // pagina.
        top: topHeader ?? top,
        position: 'fixed',
        right: rightHeader ?? MARGEN_DERECHO,
        zIndex: 900,          // debajo del header (1000) y de los modales de antd
        background: urgente ? '#a8071a' : colorPrimary,
        color: '#fff',
        // Solo las esquinas de ABAJO: la pastilla cuelga del header como una
        // pestaña, en vez de flotar suelta encima del contenido.
        borderRadius: '0 0 18px 18px',
        padding: '6px 14px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        boxShadow: '0 2px 8px rgba(0,0,0,0.22)',
        // Tabular para que los digitos no bailen de ancho al cambiar cada segundo.
        fontVariantNumeric: 'tabular-nums',
        fontSize: 15,
        fontWeight: 700,
        letterSpacing: 0.4,
        lineHeight: 1,
      }}
    >
      <ClockCircleOutlined style={{ fontSize: 14 }} />
      <span>{`${d}:${h}:${m}:${s}`}</span>
    </div>
  );
};

export default CountdownPreOfertas;
